import { ApiResponse, GlobalStore, UserStore } from '@roc/feature-app-core';
import { formatPercentage } from '@roc/ui/utils';
import { makeObservable, override, action, observable, flow, computed } from 'mobx';
import { sum } from '../../utils/utils';
import { QuoteTypeStore } from '../quoteTypeStore';
import { LoanEconomicsStore } from './loanEconomicsStore';
import {
  PURCHASE, REFINANCE, getDaysBetween, GENERIC_ERROR_MESSAGE,
  LoanSubType,
  isNil,
  isNumber,
  SINGLE_FAMILY,
  DUPLEX,
  TOWNHOME,
  CONDOMINIUM,
  PPP_5_4_3_2_1,
  PPP_0_NO_PREPAY,
  prePaymentPenaltyOptionsNoPrepayOnly,
  prePaymentPenaltyOptions0To1Prepay,
  PPP_1_0_0,
  prePaymentPenaltyOptionsSingleProperty
} from '@roc/feature-utils';
import { OneToolService } from '../../services/oneToolService';
import { BASE_RATES, FIVE_YEAR_US_TREASURY, FULLY_AMORTIZING, FULLY_AMORTIZING_LABEL, ltvMapper, MAX_RATES, MIXED_USE, MULTIFAMILY_5_PLUS, RATE_TERM_REFINANCE, TEN_YEAR_US_TREASURY } from '../../utils/constants';
import { RateScenario } from '@roc/feature-types';
import { LoanSubmissionService } from 'libs/feature-loans/src/loanSubmission/services/loanSubmissionService';
import { BorrowerLoanSubmissionService } from 'libs/feature-borrower-loan-submission/src/borrowerLoanSubmisison/services/borrowerLoanSubmissionService';

export const mapLoanPurpose = (propertyOwnership, seekingCashOut, anyDebt) => {
  if (!propertyOwnership) {
    return 'Purchase';
  } else if (propertyOwnership && anyDebt && !seekingCashOut) {
    return RATE_TERM_REFINANCE;
  } else {
    return 'Cash-Out Refinance';
  }
};

export const getAsIsValueByLoanPurpose = (loanPurpose, propertyValuation, acquisitionPrice) => {
  return loanPurpose == PURCHASE ? Math.min(propertyValuation, acquisitionPrice) : propertyValuation;
}

export const getUnitsByPropertyType = value => {
  switch (value) {
    case 'Single Family':
    case 'Townhome':
    case 'Planned Unit Development':
    case 'Condominium':
      return 1;
    case 'Duplex':
      return 2;
    case 'Triplex':
      return 3;
    case 'Quadruplex':
      return 4;
    case MULTIFAMILY_5_PLUS:
      return 5;
    case MIXED_USE:
      return 2;
    default:
      return 0;
  }
};

const mapValueToLtv = loanToValue => {
  switch (loanToValue) {
    case 5:
      return '0-5';
    case 10:
      return '6-10';
    case 15:
      return '11-15';
    case 20:
      return '16-20';
    case 25:
      return '21-25';
    case 30:
      return '26-30';
    case 35:
      return '31-35';
    case 40:
      return '36-40';
    case 45:
      return '41-45';
    case 50:
      return '46-50';
    case 55:
      return '50-55';
    case 60:
      return '55-60';
    case 65:
      return '61-65';
    case 70:
      return '66-70';
    case 75:
      return '71-75';
    case 80:
      return '76-80';
    case 85:
      return '81-85';
  }
};
export class RentalPortfolioLoanEconomicsStore extends LoanEconomicsStore {
  private exitRate = 0;
  userStore: UserStore;
  selectedQuotes: any[] = [];
  displayedQuotes: any[] = [];
  borrowerSubmissionFees: any;
  selectedLtv: number;
  loanSubmissionService: LoanSubmissionService;
  loanAmount: number;
  rateScenarios: any
  rateScenarioValidationErrors: any
  existValidationError: boolean;
  public prepaymentPenaltyOptions: any[] = prePaymentPenaltyOptionsNoPrepayOnly;

  constructor(globalStore: GlobalStore, quoteTypeStore: QuoteTypeStore, userStore: UserStore) {
    super(globalStore, quoteTypeStore);
    this.userStore = userStore;
    this.loanSubmissionService = new LoanSubmissionService();
    makeObservable(this, {
      borrowerSubmissionFees: observable,
      setValidationErrors: flow,
      fetchRatesAndLoanTermsV2: flow,
      selectedLtv: observable,
      calculateKeyRatios: override,
      mapCalculatedFields: override,
      fetchExitRate: flow,
      selectedQuotes: observable,
      displayedQuotes: observable,
      clearSelectedQuotes: action,
      fetchRatesAndLoanTermsForCustomQuote: flow,
      existValidationError: observable,
      rateScenarioValidationErrors: observable,
      fetchBorrowerSubmissionFees: flow,
      fetchRatesForCustomQuotes: flow,
      prepaymentPenaltyOptions: observable,
      getPrepayPenaltyOptions: flow,
    });
  }

  *fetchExitRate() {
    try {
      if (!this.exitRate) {
        const response = yield this.oneToolSevice.getExitRate();
        this.exitRate = response.data.data;
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE
      })
    }
  }

  caclulateFields(properties: any[], leverage: any): Record<string, number> {
    //Leverage
    const totalSellerCredit = sum(properties.filter(p => !!p.sellerCredit).map(p => p.sellerCreditAmount));

    const { loanToCost, constructionReserve } = leverage;
    let totalPurchasePrice = 0;
    if (properties?.[0]?.loanPurpose === PURCHASE) {
      totalPurchasePrice = sum(properties.map(p => p.purchasePrice)) - totalSellerCredit;
    } else {
      const seasoningDays = getDaysBetween(new Date(), new Date(properties?.[0]?.purchaseDate));
      if (seasoningDays <= 180) {
        // <= 180 days seasoning
        totalPurchasePrice = sum(properties.map(p => p.purchasePrice)) + sum(properties.map(p => p.constructionRenoSpendToDate || 0));
      } else {
        // > 180 days seasoning
        totalPurchasePrice = sum(properties.map(p => p.currentMarketValue));
      }

    }


    const totalProposedConstructionBudget = sum(
      properties.map(p => p.renovationBudget)
    );
    const totalAfterRepairValue = sum(
      properties.map(p => p.marketValueAfterCompletion)
    );

    const totalMonthlyMarketRent = sum(
      properties.map(p => p.monthlyMarketRent)
    );

    const totalAnnualTaxes = sum(
      properties.map(p => p.annualTaxes)
    );

    const totalAnnualInsurance = sum(
      properties.map(p => p.annualInsurance)
    );

    const totalMarketValue = sum(properties.map(p => p.currentMarketValue));
    const initialAdvance = totalPurchasePrice * (loanToCost / 100);
    const constructionHoldback =
      totalProposedConstructionBudget * (constructionReserve / 100);
    const totalLoanAmount = initialAdvance;
    const afterRepairLTV = (totalLoanAmount / totalAfterRepairValue) * 100;
    const calculatedLTV = (totalLoanAmount / totalMarketValue) * 100;
    const annualExitDebtService = this.exitRate * totalLoanAmount / 100;
    const exitDSCR = (totalMonthlyMarketRent * 12) / (annualExitDebtService + totalAnnualTaxes + totalAnnualInsurance);

    return {
      totalMarketValue,
      calculatedLTV,
      totalPurchasePrice,
      totalProposedConstructionBudget,
      totalAfterRepairValue,
      initialAdvance,
      constructionHoldback,
      totalLoanAmount,
      afterRepairLTV,
      loanToCost,
      exitDSCR
    };
  }


  mapCalculatedFields() {
    const manualExceptionQuoteData = this.manualExceptionQuoteFormStore.calculatedFields;
    const instaLeverageQuote = this.instaLeverageQuoteFormStore.calculatedFields;

    return {
      totalLoanAmount: instaLeverageQuote.totalLoanAmount,
      exceptionTotalLoanAmount: manualExceptionQuoteData.totalLoanAmount,
    }
  }

  calculateKeyRatios() {
    const leverage = this.getLoanEconomics();
    const calculatedFields = this.calculatedFields;

    const ltcPurchasePricePercentage = formatPercentage(leverage.loanToCost);

    return `${ltcPurchasePricePercentage}`;
  }

  validateLeverageAmounts() {
    const errors = [];

    if (!this.enableManualQuote) {
      const maxLTC = this.instaQuoteRanges?.maxLoanToCost >= 0 ? this.instaQuoteRanges?.maxLoanToCost : 0;
      const ltc = this.calculatedFields?.loanToCost || 0;

      const maxLTV = this.instaQuoteRanges?.maxARLTV;
      const ltv = this.calculatedFields?.calculatedLTV || 0;

      const exitDSCR = this.calculatedFields?.exitDSCR;
      const minExitDSCR = this.instaQuoteRanges?.minExitDSCR;

      if (!!exitDSCR && exitDSCR < minExitDSCR) {
        errors.push(`Exit DSCR should not be less than ${minExitDSCR}x.`);
      }
      if (this.quoteTypeStore.showOnlyLTVValues) {
        if (ltc > maxLTV) {
          errors.push(`The loan amount you are requesting is over ${maxLTV}% LTV. Please reduce the LTV so the loan request is ${maxLTV}% LTV or less.`);
        }
      } else {
        if (ltc > maxLTC) {
          errors.push(`The loan amount you are requesting is over ${maxLTC}% LTC. Please reduce the LTC so the loan request is ${maxLTC}% LTC or less.`);
        }

        if (ltv > maxLTV) {
          errors.push(`The loan amount you are requesting is over ${maxLTV}% LTV. Please reduce the LTV so the loan request is ${maxLTV}% LTV or less.`);
        }
      }
    }

    return errors;
  }

  clearSelectedQuotes() {
    this.selectedQuotes = [];
  }

  *fetchRatesForCustomQuotes() {
    const quotesCopy = this.displayedQuotes.slice();

    // Using normal for-loop to avoid yield operator problems
    for (let index = 0; index < quotesCopy.length; index++) {
      const quote = {
        ...quotesCopy[index],
        amortization: this.loanEconomicsPricerFormStore.form.fields.amortization.value,
        prepenaltyPayment: this.loanEconomicsPricerFormStore.form.fields.prepenaltyPayment.value,
        rateType: this.loanEconomicsPricerFormStore.form.fields.rateType.value,
      };
      yield this.fetchRatesAndLoanTermsForCustomQuote(quote, index);
    }
  }

  *fetchRatesAndLoanTermsForCustomQuote(customQuote, quoteIndex) {
    try {
      const userId = this.userStore?.userInformation?.userId;
      const {
        amortization,
        prepenaltyPayment,
        rateType,
      } = this.loanEconomicsPricerFormStore.form.fields;
      const {
        loanToValue,
        rate,
        pricerLoanTerms,
        seekingCashOut,
        anyDebt,
        pointsAndFeesPercent
      } = customQuote;
      const yieldSpreadPremium = this.borrowerSubmissionFees?.yieldSpreadPremium ?? 0;
      const { paymentChoice, chargeBorrower, loanTerm } = pricerLoanTerms;

      const ratesRequest = {
        ...this.createSinglePropertyRequest(),
        ...this.singlePropertyExpense(),
      }
      const underwritingProcessingFees = {
        underwritingProcessingFees: [(pointsAndFeesPercent * pricerLoanTerms.amount) / 100],
        buydownPoints: 0
      };
      const {
        cashOut,
        outstandingLoanOnTheProperty,
        propertyType,
        propertyOwnership,
        currentMarketValue,
        monthlyMarketRent,
        annualTaxes,
        annualInsurance,
        annualHOAProvidedAtOrigination,
        purchasePrice,
        loanPurpose,
      } = this.quoteTypeStore.propertiesStore?.propertyFormStores[0].form.fields;
      const {
        rentalExperience,
        ficoScore
      } = this.quoteTypeStore.borrowersStore?.borrowerFormStores[0].form.fields;

      const finalLoanPurpose = mapLoanPurpose(loanPurpose.value, cashOut.value, outstandingLoanOnTheProperty.value);
      const request = {
        ...ratesRequest,
        userId: userId,
        isBroker: false,
        ...underwritingProcessingFees,
        paymentChoice,
        chargeBorrower,
        loanTerm: 30,
        rateType: rateType.value,
        oneTimeYieldSpreadPremium: yieldSpreadPremium,
        prePaymentPenalty: prepenaltyPayment.value,
        currentBuydown: 0,
        buydownPoints: 0,
        loanToValue,
        amortization: amortization.value,
        loanPurpose: finalLoanPurpose,
        seekingCashOut,
        anyDebt: anyDebt == 'Y',
      };
      const response: ApiResponse = yield this.loanSubmissionService.getRatesSingleQuote(request);

      const asIsValue = getAsIsValueByLoanPurpose(finalLoanPurpose, currentMarketValue.value, purchasePrice.value);
      let newLoanToValue = loanToValue;

      let checkDone = false;
      while (!checkDone && newLoanToValue > 0) {
        const scenario = response.data;
        const amortizationData = scenario.ltvRates[rateType.value][amortization.value];
        const ltvRange = ltvMapper[newLoanToValue];
        const rateObj = amortizationData.baseRates[ltvRange];
        const rate = rateObj && rateObj?.errorMessage.length === 0 ? 1 : null;
        if (rate) {
          checkDone = true;
        } else {
          newLoanToValue -= 5;
        }
      }
      while (!checkDone && newLoanToValue <= 80) {
        const scenario = response.data;
        const amortizationData = scenario.ltvRates[rateType.value][amortization.value];
        const ltvRange = ltvMapper[newLoanToValue];
        const rateObj = amortizationData.baseRates[ltvRange];
        const rate = rateObj && rateObj?.errorMessage.length === 0 ? 1 : null;
        if (rate) {
          checkDone = true;
        } else {
          newLoanToValue += 5;
        }
      }

      const loanAmount = asIsValue * (newLoanToValue / 100);
      const scenario = response.data;
      const amortizationData = scenario.ltvRates[rateType.value][amortization.value];
      const ltvRange = ltvMapper[newLoanToValue];
      const rateObj = amortizationData.baseRates[ltvRange];
      const newRate = rateObj.errorMessage.length === 0 ? this.calculateRate(rateObj) : null;
      const newMaxRate = newRate ? this.calculateMaxRate(rateObj.swapRate, amortizationData.maxRates[ltvRange]) : 0;
      const interestOnlyPeriod = amortization.value === FULLY_AMORTIZING ? 0 : 120;
      const fullyAmortizingPeriod = isNumber(interestOnlyPeriod) ? 360 - interestOnlyPeriod : 360;

      const dscrPayload = {
        loanSubtype: LoanSubType.SINGLE_PROPERTY,
        estimatedAsIsValue: getAsIsValueByLoanPurpose(finalLoanPurpose, currentMarketValue.value, purchasePrice.value),
        requestedLTV: this.selectedLtv,
        rate: newRate,
        interestOnlyPeriod: interestOnlyPeriod,
        fullyAmortizingPeriod: fullyAmortizingPeriod,
        loanAmount: loanAmount,
        inPlaceMonthlyRent: monthlyMarketRent.value,
        monthlyMarketRent: monthlyMarketRent.value,
        annualQualifyingRent: this.calculateIncomeAndExpenses().grossPotentialRent,
        vacancy: null,
        vacancyPercentOfGPR: null,
        concession: null,
        concessionPercentOfGPR: null,
        creditLoss: null,
        creditLossPercentOfGPR: null,
        grossAnnualIncome: this.calculateIncomeAndExpenses().calculatedIncome,
        grossPotentialRent: this.calculateIncomeAndExpenses().grossPotentialRent,
        annualTaxes: annualTaxes.value,
        annualInsurance: annualInsurance.value,
        annualHOAFees: annualHOAProvidedAtOrigination.value,
        unitCount: getUnitsByPropertyType(propertyType.value),
        propertyManagementFee: null,
        propertyManagementFeePercentOfGPR: null,
        generalAndAdministrativeFee: null,
        generalAndAdministrativeFeePercentOfGPR: null,
        repairsAndMaintenance: null,
        repairsAndMaintenancePercentOfGPR: null,
        utilities: null,
        utilitiesPercentOfGPR: null,
        payroll: null,
        payrollPercentOfGPR: null,
        turnoverCosts: null,
        leasingAndMarketing: null,
        miscellaneous: null,
        totalOperatingExpensePercentOfGPR: null,
        replacementReserves: null,
        replacementReservesPercentOfGPR: null,
        requestedLtv: loanToValue,
      }
      const resp: ApiResponse = yield this.loanSubmissionService.getCalculatedValues(dscrPayload);
      const dscr = resp?.data?.data ?? {}
      const borrowerSubmissionFees = this.borrowerSubmissionFees ?? {};
      const fiveYrTsrySwap = scenario?.swapRates[FIVE_YEAR_US_TREASURY]
      const tenYrTsrySwap = scenario?.swapRates[TEN_YEAR_US_TREASURY]
      const newQuote = {
        ...customQuote,
        loanPurpose: finalLoanPurpose,
        rentalExperience: rentalExperience.value,
        ficoProvidedAtOrigination: ficoScore?.value ?? 0,
        fullScenario: scenario,
        loanToValue: newLoanToValue,
        amortizationData,
        rate: newRate,
        minRate: newRate,
        baseRate: newRate,
        maxRate: newMaxRate,
        dscr,
        borrowerSubmissionFees,
        fiveYrTsrySwap,
        tenYrTsrySwap,
        yieldSpread: newRate ? yieldSpreadPremium : 0,
        maxYieldSpread: newRate ? scenario.maxYspBasedonAmortizationType[amortization] : 0,
        pricerLoanTerms: {
          loanTerms: scenario.ltvRates,
          allInvestorRates: scenario.allInvestorRates,
          maxYspBasedonAmortizationType: scenario.maxYspBasedonAmortizationType,
          maxYsp: scenario.maxYspBasedonAmortizationType[amortization],
          overRideRates: scenario.overRideRatesBasedonAmortizationType,
        },
        addOnPoints: borrowerSubmissionFees.termOriginationPoints ?? 0,
        addOnPointsAmount: (borrowerSubmissionFees.termOriginationPoints ?? 0) / 100 * loanAmount,
      }

      if (!isNil(newQuote.rate)) {
        const newLoanTerms = yield this.fetchSfrPricerLoanTermsValuesV2(newQuote, yieldSpreadPremium);

        newQuote.pricerLoanTerms = {
          ...newQuote.pricerLoanTerms,
          ...newLoanTerms,
        };
      }
      this.displayedQuotes[quoteIndex] = { ...newQuote, cashToClose: this.calculateCashToClose(newQuote, borrowerSubmissionFees), };
    } catch (error) {
      console.log('Error setting rates for custom quote', error);
    }
  }

  createSinglePropertyRequest() {
    const {
      prepenaltyPayment,
      rateType,
      amortization,
      loanToValue
    } = this.loanEconomicsPricerFormStore?.form.fields;
    const userId = this.userStore.userInformation?.userId;
    const {
      propertyType,
      purchaseDate,
      zipCode,
      cashOut,
      section8,
      outstandingLoanOnTheProperty,
      monthlyMarketRent,
      state,
      county,
      loanPurpose,
      purchasePrice,
      currentMarketValue,
      renoBudget,
    } = this.quoteTypeStore.propertiesStore?.propertyFormStores[0].form.fields
    const {
      experience,
      bankruptcyDate,
      foreclosureDate,
      citizenshipStatus,
      ficoScore,
    } = this.quoteTypeStore.borrowersStore?.borrowerFormStores[0].form.fields;

    const formattedDate =
      purchaseDate.value && new Date(purchaseDate.value);
    const formattedBankruptcyDate =
      bankruptcyDate?.value && new Date(bankruptcyDate?.value);
    const formattedForeclosureDate =
      foreclosureDate?.value && new Date(foreclosureDate?.value);
    const borrowerExpMapping = {
      '0': '0',
      '1': '1-2',
      '2': '3-4',
      '3': '5-9',
      '4': '10-49',
      '5': '50+',
    };
    const finalLoanPurpose = mapLoanPurpose(loanPurpose.value, cashOut.value, outstandingLoanOnTheProperty.value);

    return {
      paymentChoice: 'sharePoints',
      chargeBorrower: "yieldSpreadPremiumRates",
      loanTerm: 30,
      propertyType: propertyType.value,
      asIsValue: getAsIsValueByLoanPurpose(finalLoanPurpose, currentMarketValue.value, purchasePrice.value),
      monthlyRent: monthlyMarketRent.value,
      loanPurpose: finalLoanPurpose,
      purchasePrice: purchasePrice.value,
      purchaseDate: formattedDate ? formattedDate.toISOString() : null,
      capitalImprovements: renoBudget?.value ?? 0,
      creditScore: ficoScore?.value ?? 0,
      borrowerExp: borrowerExpMapping[experience.value],
      foreignNationalFlag: citizenshipStatus.value,
      rateType: rateType.value,
      oneTimeYieldSpreadPremium: this.borrowerSubmissionFees?.yieldSpreadPremium ?? 0,
      prePaymentPenalty: prepenaltyPayment.value,
      loanSubtype: LoanSubType.SINGLE_PROPERTY,
      numberOfUnits: getUnitsByPropertyType(propertyType.value),
      commercialUnits: 0,
      residentialUnits: 0,
      commercialArea: 0,
      residentialArea: 0,
      commercialIncome: 0,
      residentialIncome: 0,
      bankruptcyDate: formattedBankruptcyDate,
      foreclosureDate: formattedForeclosureDate,
      state: state.value,
      propertyCounties: [county.value],
      commercialUnitsCurrentOnRent: 0,
      userId: userId,
      currentBuydown: 0,
      buydownFee: 0,
      loanToValue: loanToValue.value,
      amortization: amortization.value,
      zipCode: zipCode.value,
      hasSection8: section8?.value ?? false,
      isRuralFromOriginator: false,// check this
      anyDebt: outstandingLoanOnTheProperty.value,
    };
  }

  singlePropertyExpense() {
    const {
      annualTaxes,
      annualInsurance,
      annualHOAProvidedAtOrigination,
      monthlyMarketRent,
      propertyType,
      currentlyOccupied,
      loanPurpose,
    } = this.quoteTypeStore.propertiesStore?.propertyFormStores[0].form.fields;
    return {
      grossAnnualTaxes: annualTaxes.value,
      grossAnnualInsurance: annualInsurance.value,
      annualHoaDues: annualHOAProvidedAtOrigination.value,
      numberOfUnits: getUnitsByPropertyType(propertyType.value),
      monthlyRent: monthlyMarketRent.value,
      generalAdministrative: 0,
      payroll: 0,
      marketing: 0,
      replacementReserves: 0,
      totalAnnualUtilities: 0,
      totalAnnualRepairsMaintenance: 0,
      totalAnnualPropertyManagementFees: 0,
    };
  };

  calculateIncomeAndExpenses() {
    const {
      currentMarketValue,
      monthlyMarketRent,
      annualTaxes,
      annualHOAProvidedAtOrigination,
      renoBudget,
      purchasePrice,
      annualInsurance,
      currentlyOccupied,
      loanPurpose,
      cashOut,
      outstandingLoanOnTheProperty,
    } = this.quoteTypeStore.propertiesStore?.propertyFormStores[0].form.fields
    const calculatedExpenses = (annualHOAProvidedAtOrigination?.value ?? 0) + (annualTaxes?.value ?? 0) + (annualInsurance?.value ?? 0);
    const finalLoanPurpose = mapLoanPurpose(loanPurpose.value, cashOut.value, outstandingLoanOnTheProperty.value);
    const grossPotentialRent = Number(monthlyMarketRent.value) * 12;
    const calculatedIncome = grossPotentialRent;
    const calculatedOperatingIncome = calculatedIncome - calculatedExpenses;
    return {
      calculatedOperatingIncome,
      calculatedIncome,
      calculatedExpenses,
      grossPotentialRent
    }
  }

  calculateRate(data) {
    return Number((data.swapRate + data.spreadRate).toFixed(4));
  }

  calculateMaxRate(swapRate, data) {
    return Number((swapRate + data.spreadRate).toFixed(4));
  }

  calculateCashToClose(quote, fees) {
    const {
      annualTaxes,
      annualInsurance,
      outstandingLoanOnTheProperty,
      purchasePrice,
      propertyOwnership
    } = this.quoteTypeStore.propertiesStore?.propertyFormStores[0].form.fields
    const FIRST_3_MONTHS = 3;
    const YEAR_MONTHS = 12;
    const floor = {
      ZERO: 0,
      TWO_K: 200000,
      FIVE_K: 500000,
    }
    const percentages = {
      TIER_1: 0.04,
      TIER_2: 0.03,
      TIER_3: 0.02
    }
    const taxesCollectedAtClose = ((annualTaxes?.value ?? 0) / YEAR_MONTHS) * FIRST_3_MONTHS;
    const insuranceCollectedAtClose = ((annualInsurance?.value ?? 0) / YEAR_MONTHS) * FIRST_3_MONTHS;
    let thirdPartyCosts = 0;
    let deltaAmount = 0;
    if (propertyOwnership.value) {
      deltaAmount = Math.abs(quote?.pricerLoanTerms?.amount - (outstandingLoanOnTheProperty.value ?? 0));
    }
    else {
      deltaAmount = Math.abs(quote?.pricerLoanTerms?.amount - (purchasePrice.value ?? 0));
    }
    switch (quote?.pricerLoanTerms?.amount) {
      case quote?.pricerLoanTerms?.amount >= floor.ZERO && quote?.pricerLoanTerms?.amount <= floor.TWO_K:
        thirdPartyCosts = (percentages.TIER_1) * quote?.pricerLoanTerms?.amount
      case quote?.pricerLoanTerms?.amount > floor.TWO_K && quote?.pricerLoanTerms?.amount <= floor.FIVE_K:
        thirdPartyCosts = (percentages.TIER_2) * quote?.pricerLoanTerms?.amount
      default:
        thirdPartyCosts = (percentages.TIER_3) * quote?.pricerLoanTerms?.amount
    }
    return (quote?.pricerLoanTerms?.monthlyPayment * FIRST_3_MONTHS) + taxesCollectedAtClose + insuranceCollectedAtClose + deltaAmount + thirdPartyCosts + (quote?.pointsAndFees ?? 0);
  }

  *fetchSfrPricerLoanTermsValuesV2(rateScenario: RateScenario, oneTimeYieldSpreadPremium: number) {
    const rateChargedToBorrower = rateScenario.rate;

    try {
      const requestBody = this.getSfrPricerLoanTermsRequestBodyV2(rateScenario, oneTimeYieldSpreadPremium);
      const underwritingProcessingFees = {
        underwritingProcessingFees: [(rateScenario?.pointsAndFeesPercent * rateScenario?.pricerLoanTerms.amount) / 100],
        buydownPoints: 0
      };
      const response: ApiResponse = yield this.loanSubmissionService.postPricerLoanTerms(
        {
          ...requestBody,
          currentBuydown: 0,
          buydownPoints: 0,
          ...underwritingProcessingFees,
          isBroker: false,
        }
      );

      const {
        loanAmount,
        monthlyPayment,
        monthlyPaymentWithoutExpenses,
        totalPremium,
        lenderPremium,
        yieldSpreadPremiumAmount,
        minRocPoints,
        boxes,
        investorSalePrices,
        selectedLtvRate,
        submissionInvestor,
        loanTerm,
      } = response.data;

      const {
        purchasePrice,
        loanPurpose,
        cashOut,
        outstandingLoanOnTheProperty,
      } = this.quoteTypeStore.propertiesStore.propertyFormStores[0].form.fields;

      const rate = rateScenario.pricerLoanTerms?.loanTerms?.[rateScenario.rateType]?.[rateScenario.amortization]?.yieldSpreadPremiumRates[ltvMapper[rateScenario.loanToValue]];
      const finalLoanPurpose = mapLoanPurpose(loanPurpose.value, cashOut.value, outstandingLoanOnTheProperty.value);

      const loanTerms = {
        // ...this.pricerLoanTerms,
        ...{
          amount: loanAmount,
          loanPurpose: finalLoanPurpose,
          acquisitionPrice: purchasePrice.value,
          totalDebtPayoff: outstandingLoanOnTheProperty.value,
          monthlyPayment: Number(monthlyPayment?.toFixed(3)), //TODO: Use util here
          monthlyPaymentWithoutExpenses: Number(monthlyPaymentWithoutExpenses?.toFixed(3)),
          rateChargedToBorrower,
          swapRate: rate.swapRate,
          spreadRate: rate.spreadRate,
          totalPremium,
          lenderPremium,
          outOfBox: false,
          oneTimeYieldSpreadPremiumAmount: yieldSpreadPremiumAmount,
          investorId: rate?.investorId,
          minRocPoints: minRocPoints,
          boxes: boxes,
          investorSalePrices: investorSalePrices,
          selectedLtvRate,
          submissionInvestor,
          loanTerm,
        },
      };

      return loanTerms;
    } catch (error) {
      console.log('error', error)
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching loan terms.',
      });
    }
  }

  getSfrPricerLoanTermsRequestBodyV2(rateScenario: RateScenario, oneTimeYieldSpreadPremium: number) {
    const {
      rate: rateChargedToBorrower,
      loanToValue,
      rateType,
      amortization,
      prePaymentPenalty,
    } = rateScenario;
    const paymentChoice = 'sharePoints';
    const {
      propertyType,
      purchaseDate,
      cashOut,
      outstandingLoanOnTheProperty,
      loanPurpose,
      purchasePrice,
      currentMarketValue,
      renoBudget,
    } = this.quoteTypeStore.propertiesStore?.propertyFormStores[0].form.fields
    const selectedLtvRate = rateScenario.pricerLoanTerms?.loanTerms?.[rateType]?.[amortization]?.baseRates[ltvMapper[rateScenario.loanToValue]];

    let ratesForSelectedLtv = rateScenario.fullScenario.allInvestorRates[rateType][amortization];
    ratesForSelectedLtv = ratesForSelectedLtv.map(
      rate => rate?.baseRates?.[mapValueToLtv(loanToValue)]
    );
    const finalLoanPurpose = mapLoanPurpose(loanPurpose.value, cashOut.value, outstandingLoanOnTheProperty.value);
    const asIsValue = getAsIsValueByLoanPurpose(finalLoanPurpose, currentMarketValue.value, purchasePrice.value);;
    const formattedDate = purchaseDate && new Date(purchaseDate.value);

    const requestBody = {
      ltvBand: mapValueToLtv(loanToValue),
      loanToValue: loanToValue,
      asIsValue: asIsValue,
      finalRate: rateChargedToBorrower,
      selectedLtvRate,
      ratesForSelectedLtv,
      amortization: amortization,
      rateTermType:
        amortization === FULLY_AMORTIZING
          ? `${rateType} ${FULLY_AMORTIZING_LABEL}`
          : rateType,
      typeOfRate:
        oneTimeYieldSpreadPremium === 0
          ? BASE_RATES
          : MAX_RATES,
      yieldSpreadPremium: oneTimeYieldSpreadPremium,
      totalAnnualExpenses: 0, // user hoa, insurante, etc
      loanPurpose: finalLoanPurpose,
      purchasePrice: purchasePrice.value,
      paymentChoice,
      prePaymentPenalty: prePaymentPenalty,
      loanSubtype: LoanSubType.SINGLE_PROPERTY,
      loanTerm: 30,
      numberOfUnits: getUnitsByPropertyType(propertyType.value),
      residentialUnits: 0,
      commercialUnits: 0,
      totalMixUseEstimatedAsIsValue: 0,
      userId: this.userStore.userInformation?.userId,
      sfrProperty: null,
      numberOfProperties: null,
      rateBuydown: 0, //check
      purchaseDate: formattedDate ? formattedDate.toISOString() : null,
      capitalImprovements: renoBudget?.value ?? 0,
    };

    return {
      ...requestBody,
      propertyType: propertyType.value,
      ...this.singlePropertyExpense(),
    };
  }

  *fetchRatesAndLoanTermsV2() {
    try {
      const userId = this.userStore.userInformation?.userId;
      const {
        rateType,
        amortization,
        loanToValue,
      } = this.loanEconomicsPricerFormStore.form.fields;
      this.fetchBorrowerSubmissionFees();
      const ratesRequest = {
        ...this.createSinglePropertyRequest(),
        ...this.singlePropertyExpense(),
      }
      const underwritingProcessingFees = {
        underwritingProcessingFees: [],
        buydownPoints: 0
      };
      const request = {
        sfrFormData: {
          ...ratesRequest,
          userId: userId,
          isBroker: false,
          currentBuydown: 0,
          buydownPoints: 0,
          ...underwritingProcessingFees,
        },
        buydownRequestData: this.buildBuyDownRatesRequestData(),
      };
      yield this.getPrepayPenaltyOptions(ratesRequest);
      const response: ApiResponse = yield this.loanSubmissionService.getRatesScenarios(request);

      this.setRatesScenarios(response.data);

      const ltvRange = ltvMapper[loanToValue.value];
      let rateResults = [];
      const buydowns = Object.keys(response.data.scenarios).map(buydown => buydown);
      for (let key of buydowns) {
        rateResults.push(response.data.scenarios[key].ltvRates[rateType.value][amortization.value]?.baseRates[ltvRange]);
      };

      let selectedLtvRatesForValidation = {};
      for (let key of buydowns) {
        const parsedBuydown = Number(key);
        selectedLtvRatesForValidation[parsedBuydown] = response.data.scenarios[key].ltvRates[rateType.value][amortization.value]?.baseRates[ltvRange];
      };
      const errorValidationRequest = {
        sfrFormData: {
          ...request.sfrFormData,
          loanToValue: loanToValue.value,
        },
        selectedLtvRates: selectedLtvRatesForValidation,
      }
      const validationErrorResponse: ApiResponse = yield this.loanSubmissionService.getRateValidationErrors(errorValidationRequest);
      console.log({ validationErrorResponse })
      this.setValidationErrors(validationErrorResponse.data);
    } catch (error) {
      console.log('Error setting rates', error);
      this.rateScenarios = [];
    }
  }

  *setRatesScenarios(data: any) {
    const buydowns = Object.keys(data.scenarios).map(buydown => buydown).sort((a, b) => (Number(b) - Number(a)));
    const {
      rateType,
      amortization,
      prepenaltyPayment,
    } = this.loanEconomicsPricerFormStore.form.fields;
    const {
      currentMarketValue,
      purchasePrice,
      loanPurpose,
      cashOut,
      outstandingLoanOnTheProperty,
    } = this.quoteTypeStore.propertiesStore?.propertyFormStores[0].form.fields;

    const finalLoanPurpose = mapLoanPurpose(loanPurpose.value, cashOut.value, outstandingLoanOnTheProperty.value);
    const asIsValue = getAsIsValueByLoanPurpose(finalLoanPurpose, currentMarketValue.value, purchasePrice.value);
    let maxFees = 0;
    let youMakeForBroker = 0;
    let youMakeForBrokerPercent = 0;
    let loanToValue = this.loanEconomicsPricerFormStore.form.fields.loanToValue.value;

    let checkDone = false;
    while (!checkDone && loanToValue > 0) {
      const ltvRange = ltvMapper[loanToValue];
      if (ltvRange || loanToValue >= 80) {
        checkDone = true;
        this.loanEconomicsPricerFormStore.onFieldChange('loanToValue', loanToValue);
      } else {
        loanToValue += 1;
      }
    }

    this.loanAmount = asIsValue * (loanToValue / 100);

    let rates = [];

    // Using normal for-loop to avoid yield operator problems
    for (let i = 0; i < buydowns.length; i++) {
      const buydown = buydowns[i];
      const scenario = data.scenarios[buydown];
      const parsedBuydown = Number(buydown);
      const amortizationData = scenario.ltvRates[rateType.value][amortization.value];
      const ltvRange = ltvMapper[loanToValue];
      const rateObj = amortizationData.baseRates[ltvRange];
      const rate = rateObj && rateObj?.errorMessage.length === 0 ? this.calculateRate(rateObj) : null;
      const maxRate = rate ? this.calculateMaxRate(rateObj.swapRate, amortizationData.maxRates[ltvRange]) : 0;
      const pointsAndFees = Math.round((this.loanAmount * (parsedBuydown / 100)) * 100) / 100;

      const rateScenario: RateScenario = {
        fullScenario: scenario,
        buydownKey: buydown,
        buydown: parsedBuydown,
        amortizationData,
        amortization: amortization.value,
        loanToValue: loanToValue,
        prePaymentPenalty: prepenaltyPayment.value,
        rateType: rateType.value,
        rate,
        baseRate: rateObj,
        pointsAndFees,
        pointsAndFeesPercent: parsedBuydown,
        pricerLoanTerms: {
          loanTerms: scenario.ltvRates,
          allInvestorRates: scenario.allInvestorRates,
          maxYspBasedonAmortizationType: scenario.maxYspBasedonAmortizationType,
          maxYsp: scenario.maxYspBasedonAmortizationType[amortization],
          overRideRates: scenario.overRideRatesBasedonAmortizationType,
        },
        commissions: {},
        minRate: rate,
        maxRate,
        yieldSpread: this.borrowerSubmissionFees?.yieldSpreadPremium ?? 0,
        maxYieldSpread: scenario.maxYspBasedonAmortizationType[amortization.value],
        maxPointsAndFees: pointsAndFees,
        youMakeForWholesale: 0,
        youMakeForWholesalePercent: 0,
        originationPointsRetail: 0,
        originationPointsRetailAmount: 0,
        originationPointsRoc: 0,
        youMakeForBroker,
        youMakeForBrokerPercent,
        validationError: '',
      };
      // max buydown scenario
      if (i === 0) {
        maxFees = pointsAndFees;
      }

      if (!isNil(rateScenario.rate)) {
        const pricerLoanTerms = yield this.fetchSfrPricerLoanTermsValuesV2(rateScenario, this.borrowerSubmissionFees?.yieldSpreadPremium ?? 0);

        rateScenario.pricerLoanTerms = {
          ...rateScenario.pricerLoanTerms,
          ...pricerLoanTerms
        };
      }
      rates.push(rateScenario);
    }
    this.rateScenarios = rates.map(rate => ({ ...rate, maxPointsAndFees: maxFees }));
  }

  *setValidationErrors(data: any) {
    if (Object.keys(data).length > 0) {
      this.existValidationError = true;
      this.rateScenarioValidationErrors = Object.values(data)[0];
    }
    else {
      this.existValidationError = false;
      this.rateScenarioValidationErrors = ''
    }
  }

  buildBuyDownRatesRequestData() {
    const {
      propertyType,
      currentMarketValue,
      purchasePrice,
      loanPurpose,
      cashOut,
      outstandingLoanOnTheProperty,
    } = this.quoteTypeStore.propertiesStore?.propertyFormStores[0].form.fields;
    const finalLoanPurpose = mapLoanPurpose(loanPurpose.value, cashOut.value, outstandingLoanOnTheProperty.value);

    return {
      asIsValue: getAsIsValueByLoanPurpose(finalLoanPurpose, currentMarketValue.value, purchasePrice.value),
      propertyType: propertyType.value,
      loanSubtype: LoanSubType.SINGLE_PROPERTY,
      noOfUnits: getUnitsByPropertyType(propertyType.value),
      propertyTypes: null,
    }
  }

  *fetchBorrowerSubmissionFees() {
    try {
      const response: ApiResponse = yield this.loanSubmissionService.getBorrowerSubmissionFees();
      const totalAddOnFee = (response?.data?.adminFees ?? 0)
        + (response?.data?.commitmentFees ?? 0)
        + (response?.data?.processingFees ?? 0)
        + (response?.data?.underwriterFees ?? 0);
      this.borrowerSubmissionFees = { ...response.data, totalAddOnFee };

    }
    catch (error) {
      console.log('Error setting rates', error);
      this.borrowerSubmissionFees = [];
    }
  }

  *getPrepayPenaltyOptions(request) {
    const response: ApiResponse = yield this.loanSubmissionService.getPrepayOptions(request);
    this.prepaymentPenaltyOptions = response?.data || prePaymentPenaltyOptionsNoPrepayOnly;
    this.loanEconomicsPricerFormStore.onFieldChange('prepenaltyPayment', this.prepaymentPenaltyOptions[0].value);
  }
}
